/**
 * Hook de gestion du podium (leaderboard) en mode standalone.
 * Responsabilité unique : fetch initial, config, affichage temporaire (showUntil), mise à jour via socket.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { getLeaderboard } from '../../services/LeaderboardService';
import {
  LEADERBOARD_ENTER_ANIMATION_MS,
  LEADERBOARD_EXIT_ANIMATION_MS,
  LEADERBOARD_DISPLAY_DURATION_MIN_SEC,
  LEADERBOARD_DISPLAY_DURATION_MAX_SEC,
  LEADERBOARD_DISPLAY_DURATION_DEFAULT_SEC,
  LEADERBOARD_REWARD_NAME_DEFAULT,
  LEADERBOARD_TOP_COUNT,
} from './constants';
import { normalizeLeaderboardConfig } from './overlayUtils';

const bounds = {
  minSec: LEADERBOARD_DISPLAY_DURATION_MIN_SEC,
  maxSec: LEADERBOARD_DISPLAY_DURATION_MAX_SEC,
  defaultSec: LEADERBOARD_DISPLAY_DURATION_DEFAULT_SEC,
  defaultRewardName: LEADERBOARD_REWARD_NAME_DEFAULT,
};

/**
 * Hook : entries, config, showUntil, entering, exiting, et handler pour leaderboard.update.
 * @param {boolean} standalone - Mode OBS (true) ou dashboard (false)
 * @param {string} [userIdParam] - userId depuis l'URL (optionnel, pour filtrer les mises à jour)
 * @param {string} typeParam - Type de classement (ex. 'first')
 * @returns {{
 *   leaderboardEntries: Array,
 *   leaderboardConfig: { showWhen: string, displayDurationSeconds: number, rewardName: string },
 *   leaderboardShowUntil: number | null,
 *   leaderboardEntering: boolean,
 *   leaderboardExiting: boolean,
 *   handleLeaderboardUpdate: function
 * }}
 */
export function useLeaderboard(standalone, userIdParam, typeParam) {
  const [leaderboardEntries, setLeaderboardEntries] = useState([]);
  const [leaderboardEntering, setLeaderboardEntering] = useState(true);
  const [leaderboardExiting, setLeaderboardExiting] = useState(false);
  const [leaderboardConfig, setLeaderboardConfig] = useState(() =>
    normalizeLeaderboardConfig(null, bounds)
  );
  const [leaderboardShowUntil, setLeaderboardShowUntil] = useState(null);

  const leaderboardFetchedRef = useRef(false);
  const leaderboardHideTimerRef = useRef(null);
  const leaderboardConfigRef = useRef(leaderboardConfig);
  leaderboardConfigRef.current = leaderboardConfig;

  // Fetch leaderboard (top 3) et config au mount en mode standalone
  useEffect(() => {
    if (!standalone || leaderboardFetchedRef.current) return;
    leaderboardFetchedRef.current = true;
    getLeaderboard(typeParam, LEADERBOARD_TOP_COUNT)
      .then((res) => {
        if (res?.success && Array.isArray(res.data)) {
          setLeaderboardEntries(res.data);
        }
        if (res?.config && typeof res.config === 'object') {
          setLeaderboardConfig((prev) =>
            normalizeLeaderboardConfig(res.config, {
              ...bounds,
              defaultRewardName: prev.rewardName || bounds.defaultRewardName,
            })
          );
        }
      })
      .catch((err) => {
        console.warn('Leaderboard fetch:', err);
      });
  }, [standalone, typeParam]);

  // Animation d'entrée du podium : retirer la classe "entering" après 600ms
  useEffect(() => {
    if (!standalone) return;
    const t = setTimeout(
      () => setLeaderboardEntering(false),
      LEADERBOARD_ENTER_ANIMATION_MS
    );
    return () => clearTimeout(t);
  }, [standalone]);

  const handleLeaderboardUpdate = useCallback(
    (data) => {
      if (
        userIdParam != null &&
        userIdParam !== '' &&
        Number(userIdParam) !== Number(data.userId)
      ) {
        return;
      }
      if (data.type !== typeParam || !Array.isArray(data.entries)) return;

      if (data.showWhen !== undefined || data.rewardName !== undefined) {
        setLeaderboardConfig((prev) =>
          normalizeLeaderboardConfig(
            {
              showWhen: data.showWhen ?? prev.showWhen,
              displayDurationSeconds:
                data.displayDurationSeconds ?? prev.displayDurationSeconds,
              rewardName:
                (data.rewardName && String(data.rewardName).trim()) ||
                prev.rewardName,
            },
            { ...bounds, defaultRewardName: prev.rewardName }
          )
        );
      }

      setLeaderboardEntries(data.entries.slice(0, LEADERBOARD_TOP_COUNT));

      if (data.showNow === true) {
        const currentConfig = leaderboardConfigRef.current;
        const durationMs =
          (Number(data.displayDurationSeconds) ||
            currentConfig.displayDurationSeconds ||
            LEADERBOARD_DISPLAY_DURATION_DEFAULT_SEC) * 1000;
        if (leaderboardHideTimerRef.current) {
          clearTimeout(leaderboardHideTimerRef.current);
        }
        setLeaderboardExiting(false);
        setLeaderboardEntering(true);
        setLeaderboardShowUntil(Date.now() + durationMs);
        const hideTimerId = setTimeout(() => {
          setLeaderboardExiting(true);
          leaderboardHideTimerRef.current = setTimeout(() => {
            setLeaderboardShowUntil(null);
            leaderboardHideTimerRef.current = null;
          }, LEADERBOARD_EXIT_ANIMATION_MS);
        }, durationMs);
        leaderboardHideTimerRef.current = hideTimerId;
      }
    },
    [userIdParam, typeParam]
  );

  // Nettoyage du timer au démontage
  useEffect(() => {
    return () => {
      if (leaderboardHideTimerRef.current) {
        clearTimeout(leaderboardHideTimerRef.current);
        leaderboardHideTimerRef.current = null;
      }
    };
  }, []);

  return {
    leaderboardEntries,
    leaderboardConfig,
    leaderboardShowUntil,
    leaderboardEntering,
    leaderboardExiting,
    handleLeaderboardUpdate,
    leaderboardHideTimerRef,
  };
}
